import { useState, useEffect } from 'react'
import Modal from '../Modal'
import Button from '../Button'
import Toast from '../Toast'
import { getAllWarehouses } from '../../services/warehouses.service'
import { getStock, setStock } from '../../services/stock.service'

function VariantStockModal({ isOpen, onClose, variant, variantIndex, productId, onStockSaved }) {
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [selectedWarehouse, setSelectedWarehouse] = useState('')
  const [currentStock, setCurrentStock] = useState(null)
  const [addQuantity, setAddQuantity] = useState('')

  useEffect(() => {
    if (isOpen) {
      loadWarehouses()
      setAddQuantity('')
      setCurrentStock(null)
    }
  }, [isOpen])

  const loadWarehouses = async () => {
    try {
      setLoading(true)
      const data = await getAllWarehouses()
      setWarehouses(data)
      if (data.length === 1) {
        setSelectedWarehouse(data[0].id)
      }
    } catch (err) {
      setNotification({ type: 'error', message: 'Error al cargar depósitos' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedWarehouse && productId) {
      loadCurrentStock()
    }
  }, [selectedWarehouse, productId])

  const loadCurrentStock = async () => {
    try {
      const stock = await getStock(productId, selectedWarehouse, {
        size: variant?.selection?.size || undefined,
        color: variant?.selection?.color || undefined
      })
      setCurrentStock(stock?.quantity || 0)
    } catch (err) {
      setCurrentStock(0)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedWarehouse) {
      setNotification({ type: 'error', message: 'Selecciona un depósito' })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const qty = parseInt(addQuantity) || 0
    if (qty <= 0) {
      setNotification({ type: 'error', message: 'Ingresa una cantidad mayor a 0' })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)
    try {
      const newQty = (currentStock || 0) + qty
      await setStock(productId, selectedWarehouse, newQty, null, {
        size: variant?.selection?.size || null,
        color: variant?.selection?.color || null
      })

      setNotification({ type: 'success', message: 'Stock agregado correctamente' })
      setTimeout(() => {
        setNotification(null)
        if (onStockSaved) onStockSaved()
        onClose()
      }, 1500)
    } catch (err) {
      setNotification({ type: 'error', message: `Error: ${err.message}` })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  const variantLabel = `${variant?.selection?.size || ''} ${variant?.selection?.colorLabel || variant?.selection?.color || ''}`.trim()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Stock: ${variantLabel || `Variante ${variantIndex + 1}`}`}
      size="medium"
    >
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div className="form-group">
          <label htmlFor="variant-warehouse">Depósito</label>
          <select
            id="variant-warehouse"
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            required
            disabled={loading}
          >
            <option value="">Seleccionar...</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '1.5rem',
          padding: '1.5rem',
          background: 'var(--bg-light)',
          borderRadius: '10px',
          border: '2px solid #e0e0e0'
        }}>
          <div>
            <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block', color: 'var(--text-dark)' }}>
              Stock Actual
            </label>
            <span style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-color)' }}>
              {currentStock ?? '-'}
            </span>
          </div>
          <div>
            <label htmlFor="add-quantity" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block', color: 'var(--text-dark)' }}>
              Agregar
            </label>
            <input
              id="add-quantity"
              type="number"
              value={addQuantity}
              onChange={(e) => setAddQuantity(e.target.value)}
              placeholder="Cantidad"
              min="1"
              disabled={saving}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={saving || !selectedWarehouse}>
            {saving ? 'Guardando...' : 'Agregar Stock'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default VariantStockModal
